import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV } from "@/config/resources";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Logo, LogoMark } from "@/components/Logo";
import Cirrus from "@/components/Cirrus";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Bell, LogOut, Languages, Menu, X } from "lucide-react";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} strokeWidth={1.6} />;
}

export default function Layout() {
  const { t, lang, toggle } = useI18n();
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [admBlocked, setAdmBlocked] = useState(0);
  const location = useLocation();
  const groupKey = (g, gi) => g.labelKey || `g${gi}`;
  const activeGroupKey = (() => {
    for (let gi = 0; gi < NAV.length; gi++) {
      const g = NAV[gi];
      if ((g.items || []).some((it) => location.pathname.startsWith(it.to))) return groupKey(g, gi);
    }
    return null;
  })();
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("navOpenGroups") || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    if (activeGroupKey) setOpenGroups((o) => (o[activeGroupKey] ? o : { ...o, [activeGroupKey]: true }));
  }, [activeGroupKey]);
  const toggleGroup = (k) => setOpenGroups((o) => {
    const n = { ...o, [k]: !o[k] };
    try { localStorage.setItem("navOpenGroups", JSON.stringify(n)); } catch {}
    return n;
  });

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch (e) {}
  };
  useEffect(() => { loadNotifs(); const i = setInterval(loadNotifs, 30000); return () => clearInterval(i); }, []);
  useEffect(() => {
    if (hasRole("super_admin", "direction", "administration", "coordinateur", "finance")) {
      api.get("/admissions/badge").then((r) => setAdmBlocked(r.data.blocked_count || 0)).catch(() => {});
    }
  }, [hasRole]);
  const unread = notifs.filter((n) => !n.read).length;

  const doLogout = async () => { await logout(); navigate("/login"); };
  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  const activeRole = user?.active_role || (user?.roles || [])[0];
  const switchSpace = async (role) => {
    if (role === activeRole) return;
    try { await api.post("/auth/switch-space", { role }); window.location.reload(); }
    catch (e) { /* not held */ }
  };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const SidebarContent = (
    <nav className="flex-1 overflow-y-auto cirrus-scroll py-4 px-3">
      {NAV.map((group, gi) => {
        const items = group.items.filter((it) => (user?.roles || []).includes("super_admin") || it.roles.includes(activeRole));
        if (items.length === 0) return null;
        const gk = groupKey(group, gi);
        const isOpen = collapsed || !group.labelKey || openGroups[gk];
        const renderItems = () => (
          <div className="space-y-1 mt-1">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={`nav-${it.to.slice(1)}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                    isActive ? "bg-accent text-accent-foreground" : "text-slate-600 hover:bg-slate-100"
                  }`}>
                <Icon name={it.icon} className="h-[18px] w-[18px] shrink-0" />
                {!collapsed && <span className="truncate flex-1">{t(it.labelKey)}</span>}
                {!collapsed && it.to === "/admin/admissions" && admBlocked > 0 && (
                  <span data-testid="admissions-blocked-pill" className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center">{admBlocked}</span>
                )}
              </NavLink>
            ))}
          </div>
        );
        return (
          <div key={gi} className="mb-3">
            {group.labelKey && !collapsed ? (
              <button data-testid={`navgroup-${gk}`} onClick={() => toggleGroup(gk)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600">
                <span>{t(group.labelKey)}</span>
                <Icon name={isOpen ? "ChevronDown" : "ChevronRight"} className="h-3.5 w-3.5" />
              </button>
            ) : null}
            {isOpen && renderItems()}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-200 ${collapsed ? "w-[76px]" : "w-64"}`}>
        <div className="h-16 flex items-center px-4 border-b border-slate-100">
          {collapsed ? <LogoMark size={34} /> : <Logo size={34} />}
        </div>
        {SidebarContent}
        <button onClick={() => setCollapsed(!collapsed)} className="h-11 border-t border-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center">
          <Menu className="h-4 w-4" />
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white flex flex-col">
            <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
              <Logo size={32} /><button onClick={() => setMobileOpen(false)}><X className="h-5 w-5 text-slate-500" /></button>
            </div>
            {SidebarContent}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/70 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5 text-slate-600" /></button>
            <div className="hidden sm:block">
              <div className="text-sm text-slate-500">{t("welcome")},</div>
              <div className="font-display font-semibold text-slate-900 -mt-0.5">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(user?.roles || []).length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="space-selector" variant="outline" size="sm" className="gap-1.5 rounded-full">
                    {t(`role_${activeRole}`)} <span className="text-slate-400">▾</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t("welcome")}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(user.roles || []).map((r) => (
                    <DropdownMenuItem key={r} data-testid={`space-${r}`} onClick={() => switchSpace(r)}
                      className={r === activeRole ? "bg-accent" : ""}>
                      {t(`role_${r}`)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button data-testid="lang-toggle" variant="ghost" size="sm" onClick={toggle} className="gap-1.5 text-slate-600">
              <Languages className="h-4 w-4" /> {lang.toUpperCase()}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="notif-btn" variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5 text-slate-600" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{unread}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="p-0">{t("notifications")}</DropdownMenuLabel>
                  {unread > 0 && <button onClick={markAll} className="text-xs text-primary">{t("markAllRead")}</button>}
                </div>
                <DropdownMenuSeparator />
                <div className="max-h-80 overflow-y-auto cirrus-scroll">
                  {notifs.length === 0 ? <div className="px-3 py-6 text-center text-sm text-slate-400">{t("noData")}</div> :
                    notifs.slice(0, 20).map((n) => (
                      <div key={n.id} className={`px-3 py-2.5 border-b border-slate-50 ${!n.read ? "bg-sky-50/50" : ""}`}>
                        <div className="text-sm font-medium text-slate-800">{n.title}</div>
                        <div className="text-xs text-slate-500">{n.body}</div>
                      </div>
                    ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="h-9 w-9 rounded-full bg-primary text-white font-semibold text-sm flex items-center justify-center">{initials}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(user?.roles || []).map((r) => <Badge key={r} variant="secondary" className="text-[10px]">{t(`role_${r}`)}</Badge>)}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={doLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 max-w-[1500px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      <Cirrus />
    </div>
  );
}
